import React, { useState, useEffect, useRef } from 'react';
import tw from 'tailwind-styled-components';
import {
  FaArrowLeft,
  FaEdit,
  FaSave,
  FaTimes,
  FaUser,
  FaCamera,
  FaChevronRight,
} from 'react-icons/fa';  //react icons
import AddressSection from './AddressSection';
import useProfile from '../../hooks/useProfile';
import MuiSelect from '../Styles/MuiSelect';

// Validation patterns
const emailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneFormat = /^(\+91)?[6-9]\d{9}$/;

const UserDetails = ({
  isOpen,
  onClose,
  displayData,
  openOnAddAddress,
  onOpenAddressModal
}) => {
  const scrollToTopRef = useRef(null);
  const { updateProfile, changePassword, loading, error, success, setError, setSuccess } = useProfile();
  const [editForm, setEditForm] = useState({});
  const [errors, setErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '' });

  const validateField = (name, value) => {
    if (value === undefined || value === null) value = '';
    value = String(value);
    switch (name) {
      case 'first_name':
        if (!value.trim()) return 'Please enter your first name';
        if (value.trim().length < 3) return 'First name must be at least 3 characters';
        if (value.trim().length > 20) return 'First name must be less than 20 characters';
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Name can only contain letters and spaces';
        return '';
      case 'last_name':
        if (value.trim().length > 20) return 'Last name must be less than 20 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Please enter your email address';
        if (!emailFormat.test(value.trim())) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (!value.trim()) return 'Please enter your phone number';
        if (!phoneFormat.test(value.trim())) return 'Please enter a valid phone number';
        return '';
      default:
        return '';
    }
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen && displayData) {
      setEditForm({ ...displayData });
      setErrors({});
      setSuccess('');
      setError('');
    }
  }, [isOpen, displayData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    const fieldError = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const handleSaveProfile = async () => {
    const newErrors = {};
    ['first_name', 'last_name', 'email', 'phone'].forEach(field => {
      const err = validateField(field, editForm[field]);
      if (err) newErrors[field] = err;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      await updateProfile(editForm);
      // Optional: onClose(); 
    } catch (err) { }
  };

  const handleSavePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      setError('Please fill in both password fields');
      return;
    }
    try {
      await changePassword(passwordForm);
      setIsChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '' });
    } catch (err) {
      // Error handled by hook
    }
  };

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    // reset overflow scrollbar on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Scroll to top if there is an error
  useEffect(() => {
    if (scrollToTopRef.current && (error || Object.keys(errors).length > 0)) {
      scrollToTopRef.current.scrollTop = 0;
    }
  }, [error, errors]);

  if (!isOpen) return null;

  return (
    <Container>
      {/* Header */}
      <Header>
        <BackButton onClick={onClose}>
          <FaArrowLeft className="w-5 h-5" />
        </BackButton>
        <HeaderTitle>Edit Profile Information</HeaderTitle>
      </Header>

      <Content ref={scrollToTopRef}>
        {loading && <LoadingText>Loading...</LoadingText>}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        {/* Profile Picture Section */}
        <ProfileSection>
          <ProfileImageContainer>
            <ProfileImage>
              <FaUser className="w-12 h-12 text-gray-400" />
            </ProfileImage>
            <CameraIcon>
              <FaCamera className="w-4 h-4 text-gray-600" />
            </CameraIcon>
          </ProfileImageContainer>
          <UserName>
            {displayData?.first_name && displayData?.last_name
              ? `${displayData.first_name} ${displayData.last_name}`: displayData.first_name || displayData?.email || "User"
            }
          </UserName>
        </ProfileSection>

        <FormContainer>
          <FieldGroup>
            <SectionTitle>Personal Information</SectionTitle>
            <FormFieldRow>
            <FormField>
              <FormLabel>First Name <span className="text-red-500">*</span></FormLabel>
              <FormInput
                type="text"
                name="first_name"
                value={editForm?.first_name || ""}
                placeholder="Enter your first name"
                onChange={handleInputChange}
              />
              {errors.first_name && <ErrorText>{errors.first_name}</ErrorText>}
            </FormField>

            <FormField>
                <FormLabel>Last Name</FormLabel>
                <FormInput
                  type="text"
                  name="last_name"
                  value={editForm?.last_name || ''}
                  placeholder="Enter your last name"
                  onChange={handleInputChange}
                />
                {errors.last_name && <ErrorText>{errors.last_name}</ErrorText>}
              </FormField>
            </FormFieldRow>

            <FormFieldRow>
              <FormField>
                <FormLabel>Email <span className="text-red-500">*</span></FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  value={editForm?.email || ''}
                  placeholder="Enter your email"
                  onChange={handleInputChange}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </FormField>

              <FormField>
                <FormLabel>Phone <span className="text-red-500">*</span></FormLabel>
                <FormInput
                  type="tel"
                  name="phone"
                  value={editForm?.phone || ''}
                  placeholder="Enter your phone number"
                  onChange={handleInputChange}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
                {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
              </FormField>
            </FormFieldRow>

            <FormFieldRow>
              <FormField>
                <FormLabel>Date of Birth</FormLabel>
                <FormInput
                  type="date"
                  value={editForm?.date_of_birth || ''}
                  onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                />
              </FormField>

              <FormField>
                <FormLabel>Gender</FormLabel>
                <MuiSelect
                  value={editForm?.gender || ''}
                  onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                  placeholder="Select Gender"
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </FormField>
            </FormFieldRow>
            {(displayData.role === 'seller' || displayData.userType === 'seller') && (
              <FormField>
                <FormLabel>Company Name <span className="text-red-500">*</span></FormLabel>
                <FormInput
                  type="text"
                  value={editForm?.company_name || ''}
                  placeholder="Enter your company name"
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                />
              </FormField>
            )}

            <FormField>
              <FormLabel>User Type</FormLabel>
              <FormInput
                type="text"
                value={displayData.role === 'admin' ? 'Admin' :
                  displayData.role === 'seller' ? 'Seller' :
                    displayData.role === 'buyer' ? 'Buyer' :
                      displayData.userType === 'admin' ? 'Admin' :
                        displayData.userType === 'seller' ? 'Seller' :
                          displayData.userType === 'buyer' ? 'Buyer' : 'User'}
                disabled
                className="bg-gray-100 cursor-not-allowed"
              />
            </FormField>
          </FieldGroup>

          <FieldGroup className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={onOpenAddressModal}>
            <div className="flex items-center justify-between">
              <SectionTitle>Saved Addresses</SectionTitle>
              <div className="flex items-center text-kuality-blue hover:text-kuality-dark font-medium transition-colors">
                <span className="text-sm">Manage Addresses</span>
                <FaChevronRight className="w-4 h-4 ml-2" />
              </div>
            </div>
          </FieldGroup>

          <FieldGroup>
            <SectionHeader>
              <SectionTitle>Security</SectionTitle>
              {!isChangingPassword && (
                <EditButton onClick={() => setIsChangingPassword(true)}>
                  Change Password
                </EditButton>
              )}
            </SectionHeader>

            {isChangingPassword && (
              <div className="space-y-4">
                <FormField>
                  <FormLabel>Current Password</FormLabel>
                  <FormInput
                    type="password"
                    value={passwordForm.current_password}
                    placeholder="Enter current password"
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  />
                </FormField>

                <FormField>
                  <FormLabel>New Password</FormLabel>
                  <FormInput
                    type="password"
                    value={passwordForm.new_password}
                    placeholder="Enter new password"
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  />
                </FormField>

                <ActionButtons>
                  <SaveButton onClick={handleSavePassword} disabled={loading}>
                    {loading ? 'Changing...' : 'Change Password'}
                  </SaveButton>
                  <CancelButton onClick={() => setIsChangingPassword(false)}>
                    <FaTimes className="w-4 h-4 mr-2" />
                    Cancel
                  </CancelButton>
                </ActionButtons>
              </div>
            )}
          </FieldGroup>

          <FieldGroup>
            <SectionTitle>Account Information</SectionTitle>
            <InfoItem>
              <InfoLabel>Member Since</InfoLabel>
              <InfoValue>
                {displayData.createdAt ? new Date(displayData.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'Recently joined'}
              </InfoValue>
            </InfoItem>
          </FieldGroup>
        </FormContainer>
      </Content>
      <Footer>
        <SaveButton onClick={handleSaveProfile} disabled={loading}>
          <FaSave className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : 'Save'}
        </SaveButton>
      </Footer>
    </Container>
  );
};

// Styled Components
const Container = tw.div`
  fixed inset-0 bg-white z-[100] flex flex-col overflow-hidden
`;

const Header = tw.div`
  bg-white border-b border-gray-200 p-3 md:p-2 flex items-center
`;

const BackButton = tw.button`
  text-gray-600 hover:text-gray-900 mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors
`;

const HeaderTitle = tw.h1`
  text-lg md:text-xl font-medium text-gray-900
`;

const Content = tw.div`
  flex-1 overflow-y-auto bg-white p-0 pb-4
`;
// const Footer = tw.div`
//   absolute bottom-0 left-0 right-0 p-4 flex justify-center z-20 pointer-events-none
// `;

const Footer = tw.div`
  p-2 border-t border-gray-200 bg-white flex justify-center shrink-0 z-10
`;
const ProfileSection = tw.div`
  flex flex-col items-center p-6 bg-white border-b border-gray-200
`;

const ProfileImageContainer = tw.div`
  relative flex-shrink-0 mb-4
`;

const ProfileImage = tw.div`
  w-16 h-16 md:w-20 md:h-20 bg-gray-100 rounded-full flex items-center justify-center
`;

const CameraIcon = tw.div`
  absolute bottom-0 right-0 w-6 h-6 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center border border-gray-300 shadow-sm
`;

const UserName = tw.h2`
  text-lg md:text-xl font-medium text-gray-900 text-center
`;

const FormContainer = tw.div`
  w-full max-w-2xl lg:max-w-4xl xl:max-w-5xl mx-auto p-4 md:p-6 space-y-6
`;

const FieldGroup = tw.div`
  space-y-4 p-4 md:p-6 border border-gray-200 rounded-lg bg-white
`;

const SectionHeader = tw.div`
  flex items-center justify-between mb-4
`;

const SectionTitle = tw.h3`
  text-base md:text-lg font-medium text-gray-900
`;

const EditButton = tw.button`
  text-kuality-blue hover:text-kuality-dark text-sm font-medium flex items-center px-3 py-1 rounded-lg transition-colors
`;

const FormField = tw.div`
  space-y-2
`;

const FormFieldRow = tw.div`
  grid grid-cols-1 md:grid-cols-2 gap-4
`;

const FormLabel = tw.label`
  block text-sm font-medium text-gray-700
`;

const FormInput = tw.input`
  w-full px-3 py-2 md:py-3 border border-gray-300 rounded-lg focus:outline-none text-gray-900 placeholder-gray-400
`;

const ErrorText = tw.div`
  text-red-600 text-sm font-medium
`;

const InfoGrid = tw.div`
  grid grid-cols-1 md:grid-cols-2 gap-4
`;

const InfoItem = tw.div`
  space-y-1
`;

const InfoLabel = tw.label`
  block text-sm font-medium text-gray-500
`;

const InfoValue = tw.p`
  text-gray-900
`;

const AddressInfo = tw.div`
  p-4 bg-white rounded-lg border border-gray-200 text-gray-900
`;

const ActionButtons = tw.div`
  flex justify-center pt-6 border-t border-gray-200
`;

// const SaveButton = tw.button`
//   bg-kuality-blue hover:bg-kuality-dark text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px] active:scale-[0.98]
// `;

const SaveButton = tw.button`
  bg-kuality-blue hover:bg-kuality-dark text-white px-2 py-2 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-w-[130px] active:scale-[0.98]
`;
const CancelButton = tw.button`
  bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1 ml-6 rounded-lg font-medium transition-colors flex items-center justify-center
`;

const LoadingText = tw.div`
  text-center text-gray-600 py-8 font-medium
`;

const ErrorMessage = tw.div`
  text-red-700 py-3 px-4 bg-red-50 border border-red-200 rounded-lg mb-4 font-medium text-center mx-4
`;

const SuccessMessage = tw.div`
  text-green-700 py-3 px-4 bg-green-50 border border-green-200 rounded-lg mb-4 font-medium text-center mx-4
`;

export default UserDetails;